import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  MessageSquare,
  Briefcase,
  TrendingUp,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { DashboardStats } from "@shared/types";

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, featuresRes] = await Promise.all([
        apiClient.admin.stats(),
        apiClient.admin.features(),
      ]);
      setStats(statsRes.data as DashboardStats);
      setFeatures(featuresRes.data as Record<string, boolean>);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const cards = [
    ...(features.applicationManagement
      ? [
          {
            title: "New Applications",
            value: stats?.newApplications || 0,
            total: stats?.totalApplications || 0,
            icon: Users,
            href: "/admin/applications?status=new",
            color: "text-blue-500",
            bgColor: "bg-blue-500/10",
            stripe: "bg-blue-500",
          },
        ]
      : []),
    ...(features.contactManagement
      ? [
          {
            title: "New Messages",
            value: stats?.newContacts || 0,
            total: stats?.totalContacts || 0,
            icon: MessageSquare,
            href: "/admin/contacts?status=new",
            color: "text-green-500",
            bgColor: "bg-green-500/10",
            stripe: "bg-green-500",
          },
        ]
      : []),
    ...(features.vacancyManagement
      ? [
          {
            title: "Active Vacancies",
            value: stats?.activeVacancies || 0,
            total: null,
            icon: Briefcase,
            href: "/admin/vacancies",
            color: "text-purple-500",
            bgColor: "bg-purple-500/10",
            stripe: "bg-purple-500",
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-8">
      <div className="pb-6 border-b border-border/50">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          {getGreeting()}. Here's your operations overview.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <Card key={card.title} className="transition-all duration-200 hover:border-primary/30 overflow-hidden relative">
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${card.stripe}`} />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className={cn("text-3xl font-bold tabular-nums", card.value === 0 && "text-muted-foreground")}>{card.value}</span>
                {card.total !== null && (
                  <span className="text-sm text-muted-foreground">
                    of {card.total} total
                  </span>
                )}
              </div>
              <Link to={card.href}>
                <Button variant="link" className="px-0 mt-2">
                  View all <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {features.applicationManagement && (
              <Link to="/admin/applications?status=new">
                <Button variant="outline" className="transition-all duration-200 hover:border-primary/50">
                  <Users className="mr-2 h-4 w-4" />
                  Review Applications
                </Button>
              </Link>
            )}
            {features.contactManagement && (
              <Link to="/admin/contacts?status=new">
                <Button variant="outline" className="transition-all duration-200 hover:border-primary/50">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Check Messages
                </Button>
              </Link>
            )}
            {features.vacancyManagement && (
              <Link to="/admin/vacancies/new">
                <Button variant="outline" className="transition-all duration-200 hover:border-primary/50">
                  <Briefcase className="mr-2 h-4 w-4" />
                  Create Vacancy
                </Button>
              </Link>
            )}
            <Link to="/" target="_blank">
              <Button variant="outline" className="transition-all duration-200 hover:border-primary/50">View Website</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
